/**
 * MechanizationPanel — ABK-1..6, Agda anchors, and honest open status.
 *
 * Records the six mechanized theorems with their Agda names and files,
 * the POS-A obligation discharge table, the naming triad, and the
 * honest open-status note (RA-RD27-1 BridgeWitnessRegistry runtime is
 * OPEN and blocks graduation). Mechanization here is pointers, not
 * transcribed proofs.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import { AgdaSourceLink, PanelSurface, panelTokens } from "@csk/demo-ui";
import theoremsData from "../data/theorems.json" with { type: "json" };
import obligationsData from "../data/obligations.json" with { type: "json" };
const MODE_COLOR: Record<string, string> = {
  IMMUNE: panelTokens.immune,
  CONTAINED: panelTokens.contained,
  INERT: panelTokens.inert,
};
export function MechanizationPanel(): React.JSX.Element {
  const provedCount = theoremsData.theorems.filter(
    (t) => t.status === "PROVED",
  ).length;
  return (
    <PanelSurface.PanelShell
      title="Mechanization"
      subtitle="ABK-1..6 with Agda anchors, the POS-A discharge table, the naming triad, and the honest open status."
      accentColor={panelTokens.accentTeal}
    >
      <PanelSurface.Card>
        <PanelSurface.Prose className="m-0">
          The six ABK theorems are machine-checked with zero postulates. What
          follows are pointers into the Agda development — the proofs live in
          the repository, not in this panel. ABK adds no new CSK theorems; each
          result is the existing kernel pulled back to the agentic seam
          category.
        </PanelSurface.Prose>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        Status at a glance
      </PanelSurface.SectionHeading>
      <PanelSurface.KpiGrid>
        <PanelSurface.KpiBox
          label="Theorems proved"
          value={`${String(provedCount)} / ${String(theoremsData.theorems.length)}`}
          color={panelTokens.accentGreen}
          subtitle="ABK-1..ABK-6"
        />
        <PanelSurface.KpiBox
          label="Postulates"
          value="0"
          color={panelTokens.accentGreen}
          subtitle="zero · CI enforced"
        />
        <PanelSurface.KpiBox
          label="Open work"
          value="1"
          color={panelTokens.accentOrange}
          subtitle="RA-RD27-1 runtime"
        />
      </PanelSurface.KpiGrid>

      <PanelSurface.SectionHeading>
        Theorems (ABK-1..ABK-6)
      </PanelSurface.SectionHeading>
      <div className="grid grid-cols-1 gap-3">
        {theoremsData.theorems.map((t) => (
          <PanelSurface.SurfaceBox
            key={t.id}
            color={panelTokens.accentGreen}
            padding="14px 16px"
            radius={12}
          >
            <PanelSurface.BadgeGroup className="mb-1.5 items-center gap-2">
              <span className="font-mono text-[13px] font-bold text-[var(--abk-bright)]">
                {t.id}
              </span>
              <span className="text-[13px] font-semibold text-[var(--abk-text)]">
                {t.name}
              </span>
              {PanelSurface.badge(t.status, panelTokens.accentGreen)}
            </PanelSurface.BadgeGroup>
            <div className="mb-2 text-[12.5px] leading-[1.6] text-[var(--abk-text)]">
              {t.statement}
            </div>
            <div className="flex items-center gap-2">
              <code className="font-mono text-[11px] text-[var(--abk-accent)]">
                {t.agdaName}
              </code>
              <AgdaSourceLink module={t.agdaFile} />
            </div>
          </PanelSurface.SurfaceBox>
        ))}
      </div>

      <PanelSurface.SectionHeading>
        POS-A obligation discharge
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.StatusList
          ariaLabel="POS-A obligation discharge status"
          items={obligationsData.obligations.map((o) => ({
            id: o.id,
            title: `${o.id} · ${o.name}`,
            status: o.mode,
            tone: "complete",
            color: MODE_COLOR[o.mode] ?? panelTokens.accent,
            code: o.witness,
            description: o.discharge,
            meta: o.cskTheorem,
          }))}
        />
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Honest status</PanelSurface.SectionHeading>
      <PanelSurface.Callout
        title={`${theoremsData.openStatus.id} ${theoremsData.openStatus.title} — ${theoremsData.openStatus.status}`}
        tone="warn"
      >
        {theoremsData.openStatus.note}
      </PanelSurface.Callout>

      <PanelSurface.SectionHeading>Naming triad</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.FactList
          ariaLabel="RD-27 naming triad"
          items={[
            {
              id: "security",
              label: "Security",
              value: theoremsData.namingTriad.security,
            },
            {
              id: "governance",
              label: "Governance",
              value: theoremsData.namingTriad.governance,
            },
            {
              id: "mathematical",
              label: "Mathematical",
              value: theoremsData.namingTriad.mathematical,
            },
          ]}
        />
      </PanelSurface.Card>

      <PanelSurface.RepositoryLinks
        items={[
          {
            label: "RD-27 §8–§9 Theorems & status",
            path: "research/settled/RD-27-AGENTIC-BOUNDARY-KERNEL.md",
          },
          {
            label: "BridgeWitnessSuite.agda",
            path: "agda/Research/AgenticBoundaryKernel/BridgeWitnessSuite.agda",
          },
          {
            label: "SeamObligations.agda",
            path: "agda/Research/AgenticBoundaryKernel/SeamObligations.agda",
          },
          {
            label: "ReachabilitySlice.agda",
            path: "agda/Research/AgenticBoundaryKernel/ReachabilitySlice.agda",
          },
        ]}
      />
    </PanelSurface.PanelShell>
  );
}
